import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { IconX } from "@tabler/icons-react";
import { observer } from "mobx-react";
import {
  GuardedDialogRoot,
  usePopupChangeListeners,
  type GuardedRootProps,
} from "@/components/ui/unsaved-changes";

/** `dismissible={false}` makes the dialog refuse Escape and a click on the backdrop, so
 *  the only way out is an action the person chose — a footer button, or the corner close
 *  if it is still shown. Use it where a stray click would destroy something the person
 *  cannot get back (an unrecoverable form field, a one-time secret) or would silently
 *  abandon a destructive decision.
 *
 *  It lives here because base-ui has no such prop of its own at this version, so every
 *  caller that wanted the rule wrote it again — and wrote it *differently*: two spell it
 *  `details.cancel()` on the two reasons, while `oauth/rotate-secret-modal.tsx` returns
 *  early and leans on the controlled `open` prop never changing. Three spellings of one
 *  rule is how they drift apart. Declaring it is also self-documenting in a way a handler
 *  body is not.
 *
 *  It deliberately does NOT block the close button or a `DialogClose`: those are the
 *  deliberate exits, and a dialog with no exit at all is a trap. Pair it with
 *  `showCloseButton={false}` on the content when the footer owns the only way out.
 *
 *  **Unsaved changes (NG-2717).** Once the person changes something inside the dialog,
 *  Escape and an outside press open "Discard changes?" instead of closing. Typing is
 *  detected on its own; a click-only control must call `useMarkUnsavedChange()`.
 *  `unsavedChanges` replaces detection: pass `false` where a prompt would be wrong (the
 *  dialog saves as you go, is read-only, or runs its own discard check), or the dialog's
 *  own dirty flag where it has one. The close button and `DialogClose` are never guarded.
 *  The close rules live in `GuardedDialogRoot`, shared with `Sheet`.
 */
const Dialog = observer(function Dialog(props: GuardedRootProps) {
  return <GuardedDialogRoot data-slot="dialog" {...props} />;
});

const DialogTrigger = observer(function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
});

const DialogPortal = observer(function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
});

const DialogClose = observer(function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
});

const DialogOverlay = observer(function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 isolate z-50 bg-overlay/80 transition-opacity duration-(--motion-duration-base) ease-motion-out data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs",
        className,
      )}
      {...props}
    />
  );
});

const DialogContent = observer(function DialogContent({
  className,
  children,
  showCloseButton = true,
  closeLabel = "Close",
  stacked = false,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  closeLabel?: string;
  /** Drawn on top of another open dialog (the "Discard changes?" prompt). base-ui
   *  skips a nested dialog's backdrop, which leaves it floating on the form beneath with
   *  nothing behind it; this forces a lighter backdrop of its own (NG-2717, option B).
   *  Backdrop only: the form stepping back comes from the unsaved-changes guard
   *  (`data-discard-prompt-open`), not from this prop. */
  stacked?: boolean;
}) {
  const { popupProps, prompt } = usePopupChangeListeners(props);
  return (
    <DialogPortal>
      <DialogOverlay
        forceRender={stacked}
        className={cn(stacked && "bg-overlay/45 supports-backdrop-filter:backdrop-blur-[2px]")}
      />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          // Cap every dialog at 80% of the viewport and lay it out as a flex
          // column so a tall body never grows past the screen (issue 1806).
          // Pair a `<DialogBody>` (scrollable, flex-1) with `<DialogHeader>` /
          // `<DialogFooter>` (shrink-0) to keep the header and footer fixed
          // while only the body scrolls. Without a DialogBody the whole content
          // still scrolls via `overflow-y-auto` so no modal can overflow.
          "fixed top-1/2 left-1/2 z-50 flex max-h-[80vh] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-xl bg-popover p-4 text-xs/relaxed text-popover-foreground ring-1 ring-foreground/10 transition duration-(--motion-duration-base) ease-motion-out outline-none data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 sm:max-w-sm",
          // While "Discard changes?" is open over this form, the form steps back.
          "data-discard-prompt-open:scale-95",
          className,
        )}
        {...popupProps}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={<Button variant="ghost" className="absolute top-2 right-2" size="icon-sm" />}
          >
            <IconX stroke={2} />
            <span className="sr-only">{closeLabel}</span>
          </DialogPrimitive.Close>
        )}
        {prompt}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
});

const DialogHeader = observer(function DialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex shrink-0 flex-col gap-1", className)}
      {...props}
    />
  );
});

// Scrollable body region. In the DialogContent flex column it takes the
// remaining height (`flex-1`) and scrolls, leaving the header/footer fixed
// (issue 1806). The negative inline margins + matching padding let the
// scrollbar sit at the dialog edge while content keeps the dialog's padding.
const DialogBody = observer(function DialogBody({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-body"
      className={cn("-mx-4 flex-1 overflow-y-auto px-4", className)}
      {...props}
    />
  );
});

const DialogFooter = observer(function DialogFooter({
  className,
  showCloseButton = false,
  closeLabel = "Close",
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
  closeLabel?: string;
}) {
  return (
    <div
      data-slot="dialog-footer"
      // Keep `sm:flex-wrap` alongside `sm:flex-row`. `Button` is `shrink-0
      // whitespace-nowrap`, so footer actions can neither narrow nor reflow;
      // unwrapped, a row wider than the dialog overflows off the *leading*
      // edge (`sm:justify-end` packs it rightward), where no scroll can reach
      // it — leaving the first actions unreadable and unclickable (NG-920).
      className={cn(
        "flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          {closeLabel}
        </DialogPrimitive.Close>
      )}
    </div>
  );
});

const DialogTitle = observer(function DialogTitle({
  className,
  ...props
}: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("font-heading text-sm font-medium", className)}
      {...props}
    />
  );
});

const DialogDescription = observer(function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-xs/relaxed text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
});

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
